import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/Logo';
import { GlassCard } from '@/components/GlassCard';
import { Loader2 } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { StoreBadges } from '@/components/StoreBadges';

export default function LoginPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success('Welcome back to Thirrsty');
      const dest = loc.state?.from || '/';
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen th-hero-radial">
      <Seo
        title="Sign In | Thirrsty.com"
        description="Sign in to Thirrsty.com to access your free profile, your personal QR code, the live nightlife timeline, and instant discounts at partner bars, clubs, lounges, coffee shops, vineyards, and breweries."
        path="/auth/login"
      />
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-6 py-10">
        <div className="mb-6"><Logo asDownloadTrigger /></div>
        <GlassCard className="p-6 th-float-in">
          <h1 className="font-display text-3xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Join the movement.</h1>
          <p className="mt-1 text-sm text-white/70">Sign in to access your QR & nightlife feed.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-white/80">Email</Label>
              <Input
                id="email" type="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@thirrsty.com"
                className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <Input
                id="password" type="password" required minLength={6}
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 h-11 bg-white/5 border-white/12 text-white placeholder:text-white/40"
                data-testid="auth-password-input"
              />
            </div>
            <Button
              type="submit" disabled={loading}
              data-testid="auth-login-submit-button"
              className="h-11 w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065] font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 th-spin" /> : 'Sign in'}
            </Button>
          </form>
          <p className="mt-4 text-center text-sm text-white/60">
            <Link to="/auth/forgot" className="text-[#F6B24A] hover:underline" data-testid="auth-forgot-password-link">Forgot password?</Link>
          </p>
          <p className="mt-2 text-center text-sm text-white/60">
            New to Thirrsty?{' '}
            <Link to="/auth/signup" className="text-[#F6B24A] hover:underline" data-testid="auth-go-signup-link">Create an account</Link>
          </p>
        </GlassCard>
        <div className="mt-8">
          <StoreBadges size="md" variant="row" showLabel testIdPrefix="login-store-badges" />
        </div>
        <p className="mt-auto pt-8 text-center text-xs text-white/40">
          © Banks Class LLC · <Link to="/contact" className="underline">Contact</Link> · <Link to="/privacy" className="underline" data-testid="login-privacy-link">Privacy</Link> · <Link to="/terms" className="underline" data-testid="login-terms-link">Terms</Link>
        </p>
      </div>
    </div>
  );
}
